import { useEffect, useState } from "react"

import type { AgentRecommendationPayload } from "@productlane/lib/src/agent-recommendations"

import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useAuthData } from "@/lib/zero"
import {
	EXAMPLE_BACKFILL_TIMEOUT_MS,
	shouldRequestExampleAnswers,
} from "./idea-examples-model"

// Module level, so reopening an idea in the same session never asks twice.
const requested = new Set<string>()
const startedAt = new Map<string, number>()

/**
 * Ideas from before example answers were stored ask the server for them once.
 * Returns when the request started, or null when nothing is pending.
 */
export function useExampleAnswersBackfill(
	recommendationId: string,
	payload: AgentRecommendationPayload | null | undefined,
): { startedAt: number | null; now: number } {
	const authData = useAuthData()
	const mutation = api.ai.ensureAgentRecommendationExamples.useMutation()
	const { mutateAsync } = mutation
	const [now, setNow] = useState(() => Date.now())
	const needsRequest = shouldRequestExampleAnswers(
		recommendationId,
		payload,
		requested,
	)

	useEffect(() => {
		if (!needsRequest) return
		requested.add(recommendationId)
		startedAt.set(recommendationId, Date.now())
		setNow(Date.now())
		mutateAsync({ recommendationId })
			.then(({ queued }) => {
				if (!queued) startedAt.delete(recommendationId)
				setNow(Date.now())
			})
			.catch((error: unknown) => {
				startedAt.delete(recommendationId)
				setNow(Date.now())
				captureError(error, {
					domain: "ai-chat",
					action: "backfill agent idea examples",
					workspaceId: authData.workspaceId,
					userId: authData.userId,
					extra: { recommendationId },
				})
			})
	}, [needsRequest, recommendationId, mutateAsync, authData])

	const started = startedAt.get(recommendationId) ?? null
	// One re-render at the timeout, so the loading column gives way on its own.
	useEffect(() => {
		if (started === null) return
		const left = started + EXAMPLE_BACKFILL_TIMEOUT_MS - Date.now()
		if (left <= 0) return
		const timer = setTimeout(() => setNow(Date.now()), left + 50)
		return () => clearTimeout(timer)
	}, [started])

	return { startedAt: started, now }
}
